import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { SourceControl } from "@/components/origination/SourceControl";
import { CoiFirmPicker } from "@/components/coi/CoiFirmPicker";
import type { OriginationInput } from "@shared/originationConstants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Building2, Briefcase, Handshake, BarChart3, Check, Plus, X, Loader2, Target, Banknote } from "lucide-react";
import { cn } from "@/lib/utils";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import { DuplicateContactError, throwContactWriteError, toastDuplicateContact } from "@/lib/duplicateContact";

interface AddCompanyDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  preselectedCategory?: "buyer" | "sell-side" | "coi" | "clients" | "prospects" | "capital-providers" | null;
  /** Pre-filled source, e.g. from "New prospect from this COI". */
  initialOrigination?: OriginationInput | null;
  /** Renders the source read-only; the person is already decided. */
  lockedSource?: { sourceType: "coi_referral" | "advocate_referral"; contactId: string; contactName: string; firmName: string | null } | null;
  onCreated?: (companyId: string) => void;
  /** COI flow only: open with the firm already chosen (the firm page's Add person). */
  initialCoiFirm?: { firmCompanyId: string; firmName: string } | null;
}

const categories = [
  { id: "buyer", icon: Building2, title: "Buyer Network", description: "PE firms, strategic acquirers" },
  { id: "clients", icon: Briefcase, title: "Clients", description: "M&A, capital raises, debt" },
  { id: "prospects", icon: Target, title: "Prospects", description: "Potential clients in outreach" },
  { id: "coi", icon: Handshake, title: "COI", description: "Lawyers, CPAs, bankers" },
  { id: "capital-providers", icon: Banknote, title: "Capital Providers", description: "Mezz, ABL, senior debt, equity co-invest" },
];

// Canonical target-industry ENUM — keep in sync with the enrichment
// prompt in server/routes.ts and the filter dropdown in BuyerNetwork.tsx.
const industries = [
  "Business Services",
  "Construction & Infrastructure",
  "Consumer",
  "Energy",
  "Financial Services",
  "Food & Beverage",
  "Healthcare",
  "Industrial",
  "Manufacturing",
  "SaaS & Technology",
  "Technology",
  "Transportation & Logistics",
  "Other",
];
// Canonical buyer firm types per shared/schema.ts + CLAUDE.md.
// Legacy "Strategic"/"Financial"/"Sponsor" values have been migrated
// out of the database.
const buyerSubTypes = [
  "Private Equity",
  "Strategic Acquirer",
  "Independent Sponsor",
  "Family Office",
  "Search Fund",
  "Other",
];
const clientStatuses = ["Prospecting", "Active"];
const engagementTypes = ["M&A", "Capital Raise", "Debt Placement", "Restructuring", "ESOP", "Other"];
const coiSpecialtyOptions = [
  { label: "Attorney", value: "attorney" },
  { label: "CPA", value: "cpa" },
  { label: "Banker", value: "banker" },
  { label: "Wealth Manager", value: "wealth_advisor" },
  { label: "Consultant", value: "consultant" },
  { label: "Insurance", value: "insurance" },
];
const coiSpecialtyValues = coiSpecialtyOptions.map(o => o.value);

function IndustryCombobox({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState(value);

  const filtered = industries.filter(i => i.toLowerCase().includes(search.toLowerCase()));
  const showCustom = search.trim() && !industries.some(i => i.toLowerCase() === search.toLowerCase());

  return (
    <div className="space-y-2">
      <Label>Industry</Label>
      <div className="relative">
        <Input
          value={search}
          onChange={(e) => { setSearch(e.target.value); setOpen(true); onChange(e.target.value); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Type or select an industry..."
          className="h-11"
          data-testid="input-industry"
        />
        {open && (filtered.length > 0 || showCustom) && (
          <div className="absolute z-50 top-full mt-1 w-full bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
            {filtered.map(ind => (
              <button
                key={ind}
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { setSearch(ind); onChange(ind); setOpen(false); }}
                className={cn(
                  "w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors",
                  value === ind && "bg-accent font-medium"
                )}
              >
                {ind}
              </button>
            ))}
            {showCustom && (
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => { onChange(search.trim()); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors text-primary-text font-medium border-t"
              >
                + Use "{search.trim()}"
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export function AddCompanyDrawer({ open, onOpenChange, preselectedCategory, initialOrigination, lockedSource, onCreated, initialCoiFirm }: AddCompanyDrawerProps) {
  const normalizedCategory = preselectedCategory === "sell-side" ? "clients" : preselectedCategory === "prospects" ? "prospects" : preselectedCategory === "capital-providers" ? "capital-providers" : preselectedCategory;
  const [selectedCategory, setSelectedCategory] = useState<string | null>(normalizedCategory || null);
  const [companyName, setCompanyName] = useState("");
  const [industry, setIndustry] = useState("");
  const [subType, setSubType] = useState("");
  const [engagementType, setEngagementType] = useState("");
  const [website, setWebsite] = useState("");
  const [addContact, setAddContact] = useState("yes");
  const [contacts, setContacts] = useState([{ id: 1, firstName: "", lastName: "", email: "", phone: "", title: "" }]);

  // COI-specific state (contact-first)
  const [coiFirstName, setCoiFirstName] = useState("");
  const [coiLastName, setCoiLastName] = useState("");
  const [coiEmail, setCoiEmail] = useState("");
  const [coiPhone, setCoiPhone] = useState("");
  const [coiTitle, setCoiTitle] = useState("");
  // Existing company id, or a name the server matches or creates (as a COI-category company) on save.
  const [coiFirm, setCoiFirm] = useState<{ firmCompanyId?: string; firmName?: string }>(initialCoiFirm ?? {});
  const [coiSpecialty, setCoiSpecialty] = useState("");
  const [coiCustomSpecialty, setCoiCustomSpecialty] = useState("");
  const [coiCoverage, setCoiCoverage] = useState("");
  const queryClient = useQueryClient();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  // Source by path: the form defaults to the creator's network unless a
  // caller pre-filled it (e.g. "New prospect from this COI").
  const [origination, setOrigination] = useState<OriginationInput | null>(initialOrigination ?? null);
  useEffect(() => { if (open) setOrigination(initialOrigination ?? null); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const createCompanyMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      category: string;
      subType?: string;
      status?: string;
      engagementType?: string;
      industry?: string;
      website?: string;
      contacts?: Array<{ firstName: string; lastName: string; email: string; phone?: string; title?: string }>;
      origination?: OriginationInput | null;
    }) => {
      const res = await fetch("/api/companies", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        if (res.status === 401) {
          throw new Error("Please log in to add companies. Click the Login button in the top right.");
        }
        const error = await res.json().catch(() => ({ error: "Failed to create company" }));
        throw new Error(error.error || "Failed to create company");
      }
      return res.json();
    },
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["clients"] });
      queryClient.invalidateQueries({ queryKey: ["/api/prospects"] });
      queryClient.invalidateQueries({ queryKey: ["cois"] });
      queryClient.invalidateQueries({ queryKey: ["coi"] });
      queryClient.invalidateQueries({ queryKey: ["coi-bd"] });
      const createdId = data?.company?.id ?? data?.id;
      if (createdId && onCreated) onCreated(createdId);
      queryClient.invalidateQueries({ queryKey: ["/api/capital-providers"] });
      toast({ title: "Company created", description: "The company has been added successfully." });
      handleClose();
    },
    onError: (error: Error) => {
      console.error("Company creation error:", error.message);
      toast({ 
        title: "Error", 
        description: error.message, 
        variant: "destructive",
        duration: 5000,
      });
    },
  });

  const createCoiMutation = useMutation({
    mutationFn: async (data: {
      name: string;
      title?: string;
      email?: string;
      phone?: string;
      firmName?: string;
      firmCompanyId?: string;
      employerName?: string;
      specialty: string;
    }) => {
      const res = await fetch("/api/cois", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        await throwContactWriteError(res, "Failed to create COI");
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cois"] });
      queryClient.invalidateQueries({ queryKey: ["coi-firm"] });
      queryClient.invalidateQueries({ queryKey: ["coi-bd"] });
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast({ title: "COI added", description: "The contact has been added to COI." });
      handleClose();
    },
    onError: (error: Error) => {
      if (error instanceof DuplicateContactError) return toastDuplicateContact(error.body, navigate);
      toast({ title: "Error", description: error.message, variant: "destructive", duration: 5000 });
    },
  });

  const handleClose = () => {
    setSelectedCategory(normalizedCategory || null);
    setCompanyName("");
    setWebsite("");
    setIndustry("");
    setSubType("");
    setEngagementType("");
    setAddContact("yes");
    setContacts([{ id: 1, firstName: "", lastName: "", email: "", phone: "", title: "" }]);
    setCoiFirstName("");
    setCoiLastName("");
    setCoiEmail("");
    setCoiPhone("");
    setCoiTitle("");
    setCoiFirm(initialCoiFirm ?? {});
    setCoiSpecialty("");
    setCoiCustomSpecialty("");
    setCoiCoverage("");
    onOpenChange(false);
  };

  const handleSave = () => {
    if (!selectedCategory) {
      toast({ title: "Missing fields", description: "Please select a category.", variant: "destructive" });
      return;
    }

    // COI: contact-first flow, goes to /api/cois
    if (selectedCategory === "coi") {
      if (!coiFirstName.trim() || !coiLastName.trim()) {
        toast({ title: "Missing fields", description: "Please enter first and last name.", variant: "destructive" });
        return;
      }
      if (!coiEmail?.trim()) {
        toast({ title: "Missing fields", description: "Please enter an email address.", variant: "destructive" });
        return;
      }
      const finalSpecialty = coiSpecialty === "custom" ? "other" : coiSpecialty;
      if (!finalSpecialty && !coiCustomSpecialty.trim()) {
        toast({ title: "Missing fields", description: "Please select or type a specialty.", variant: "destructive" });
        return;
      }
      createCoiMutation.mutate({
        name: `${coiFirstName.trim()} ${coiLastName.trim()}`,
        title: coiTitle || undefined,
        email: coiEmail || undefined,
        phone: coiPhone || undefined,
        ...coiFirm,
        employerName: coiFirm.firmName || undefined,
        specialty: finalSpecialty || "other",
        ...(coiSpecialty === "custom" && coiCustomSpecialty.trim() ? { specialtyNotes: coiCustomSpecialty.trim() } : {}),
        ...(coiCoverage ? { geographicCoverage: coiCoverage } : {}),
      });
      return;
    }

    // Company flow (buyer, clients, prospects)
    if (!companyName.trim()) {
      toast({ title: "Missing fields", description: "Please enter a company name.", variant: "destructive" });
      return;
    }

    if (selectedCategory === "buyer" && !subType) {
      toast({ title: "Missing fields", description: "Please select a type for this company.", variant: "destructive" });
      return;
    }

    if (selectedCategory === "clients" && !engagementType) {
      toast({ title: "Missing fields", description: "Please select an engagement type.", variant: "destructive" });
      return;
    }

    if (selectedCategory === "prospects" && !website.trim()) {
      toast({ title: "Missing fields", description: "Please enter the company website so we can enrich their data.", variant: "destructive" });
      return;
    }

    // Buyers require at least one contact; clients and prospects can skip
    const contactRequired = selectedCategory === "buyer";
    if (contactRequired || addContact === "yes") {
      const firstContact = contacts[0];
      if (!firstContact.firstName || !firstContact.lastName || !firstContact.email) {
        toast({ title: "Missing fields", description: "Please enter at least a name and email for the primary contact.", variant: "destructive" });
        return;
      }
    }

    const contactsToSave = (contactRequired || addContact === "yes")
      ? contacts.filter(c => c.firstName && c.lastName && c.email)
      : [];

    let categoryForDb = selectedCategory;
    if (selectedCategory === "clients") {
      categoryForDb = "Client";
    } else if (selectedCategory === "prospects") {
      categoryForDb = "Prospects";
    } else if (selectedCategory === "capital-providers") {
      categoryForDb = "Capital Providers";
    }

    createCompanyMutation.mutate({
      name: companyName.trim(),
      category: categoryForDb,
      subType: selectedCategory === "buyer" ? subType : undefined,
      engagementType: (selectedCategory === "clients" || selectedCategory === "prospects") ? engagementType : undefined,
      industry: industry || undefined,
      website: website.trim() || undefined,
      origination: selectedCategory === "prospects" ? origination : undefined,
      contacts: contactsToSave,
    });
  };

  const addAnotherContact = () => {
    setContacts([...contacts, { id: Date.now(), firstName: "", lastName: "", email: "", phone: "", title: "" }]);
  };

  const removeContact = (id: number) => {
    if (contacts.length > 1) {
      setContacts(contacts.filter(c => c.id !== id));
    }
  };

  const updateContact = (id: number, field: string, value: string) => {
    setContacts(contacts.map(c => c.id === id ? { ...c, [field]: value } : c));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[640px] max-h-[90vh] flex flex-col p-0" data-testid="add-company-drawer">
        <DialogHeader className="px-6 py-5 border-b border-border flex-shrink-0">
          <DialogTitle className="text-xl font-semibold">
            {preselectedCategory === "clients" ? "Add Client" : preselectedCategory === "prospects" ? "Add Prospect" : preselectedCategory === "coi" ? "Add COI" : preselectedCategory === "buyer" ? "Add Buyer" : preselectedCategory === "capital-providers" ? "Add Capital Provider" : "Add Company"}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6 space-y-6">
          {/* Category selector — only show when opened from global Add menu (no preselected category) */}
          {!preselectedCategory && (
          <div className="space-y-4">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Category <span className="text-red-500">*</span>
              </Label>
              <p className="text-sm text-muted-foreground mt-1">What type of company is this?</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              {categories.map((cat) => {
                const Icon = cat.icon;
                const isSelected = selectedCategory === cat.id;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setSelectedCategory(cat.id)}
                    className={cn(
                      "relative p-5 rounded-xl border-2 text-center transition-all duration-150",
                      isSelected
                        ? "gold-gradient border-transparent shadow-lg"
                        : "bg-card border-border hover:border-primary hover:bg-primary/5"
                    )}
                    data-testid={`category-${cat.id}`}
                  >
                    {isSelected && (
                      <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-white/30 flex items-center justify-center">
                        <Check className="w-3 h-3 text-white" />
                      </div>
                    )}
                    <Icon className={cn("w-8 h-8 mx-auto mb-2", isSelected ? "text-white" : "text-secondary")} />
                    <p className={cn("font-semibold text-sm", isSelected ? "text-white" : "text-foreground")}>{cat.title}</p>
                    <p className={cn("text-xs mt-1", isSelected ? "text-white/80" : "text-muted-foreground")}>{cat.description}</p>
                  </button>
                );
              })}
            </div>
          </div>
          )}

          {/* COI: contact-first form */}
          {selectedCategory === "coi" && (
            <div className="space-y-5 animate-fade-in">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-1 block">
                  Contact Information
                </Label>
                <p className="text-sm text-muted-foreground">COIs are people — enter their details below.</p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>First Name <span className="text-red-500">*</span></Label>
                  <Input value={coiFirstName} onChange={(e) => setCoiFirstName(e.target.value)} className="h-10" placeholder="Jane" />
                </div>
                <div className="space-y-2">
                  <Label>Last Name <span className="text-red-500">*</span></Label>
                  <Input value={coiLastName} onChange={(e) => setCoiLastName(e.target.value)} className="h-10" placeholder="Smith" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Specialty <span className="text-red-500">*</span></Label>
                <div className="flex flex-wrap gap-2">
                  {coiSpecialtyOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => { setCoiSpecialty(opt.value); setCoiCustomSpecialty(""); }}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                        coiSpecialty === opt.value
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background hover:bg-muted border-border"
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setCoiSpecialty("custom")}
                    className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                      coiSpecialty === "custom"
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background hover:bg-muted border-border"
                    }`}
                  >
                    Other...
                  </button>
                </div>
                {coiSpecialty === "custom" && (
                  <Input
                    value={coiCustomSpecialty}
                    onChange={(e) => setCoiCustomSpecialty(e.target.value)}
                    placeholder="Type specialty (e.g., Real Estate Broker)"
                    className="h-10 mt-2"
                    autoFocus
                  />
                )}
              </div>

              <div className="space-y-2">
                <Label>Title / Role</Label>
                <Input value={coiTitle} onChange={(e) => setCoiTitle(e.target.value)} className="h-10" placeholder="e.g., Partner" />
              </div>

              <div className="space-y-2">
                <Label>Firm / Company</Label>
                <CoiFirmPicker value={coiFirm} onChange={setCoiFirm} />
              </div>

              <div className="space-y-2">
                <Label>Geographic Coverage</Label>
                <Input value={coiCoverage} onChange={(e) => setCoiCoverage(e.target.value)} className="h-10" placeholder="e.g., Dallas/Fort Worth M&A" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Email <span className="text-red-500">*</span></Label>
                  <Input type="email" value={coiEmail} onChange={(e) => setCoiEmail(e.target.value)} className="h-10" placeholder="jane@firm.com" />
                </div>
                <div className="space-y-2">
                  <Label>Phone</Label>
                  <Input type="tel" value={coiPhone} onChange={(e) => setCoiPhone(e.target.value)} className="h-10" />
                </div>
              </div>

            </div>
          )}

          {/* Company flow: buyer, clients, prospects */}
          {selectedCategory && selectedCategory !== "coi" && (
            <div className="space-y-5 animate-fade-in">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground mb-4 block">
                  Company Information
                </Label>
              </div>

              <div className="space-y-2">
                <Label htmlFor="companyName">Company Name <span className="text-red-500">*</span></Label>
                <Input
                  id="companyName"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  placeholder="e.g., Apex Private Equity"
                  className="h-11 focus:border-primary focus:ring-primary/20"
                  data-testid="input-company-name"
                />
              </div>

              {selectedCategory === "buyer" && (
                <div className="space-y-2">
                  <Label>Sub-Type <span className="text-red-500">*</span></Label>
                  <RadioGroup value={subType} onValueChange={setSubType} className="flex gap-4">
                    {buyerSubTypes.map((type) => (
                      <div key={type} className="flex items-center space-x-2">
                        <RadioGroupItem value={type} id={type} className="border-primary text-primary-text" />
                        <Label htmlFor={type} className="font-normal cursor-pointer">{type}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}

              {selectedCategory === "prospects" && (
                <>
                  <div className="space-y-2">
                    <Label>Website <span className="text-red-500">*</span></Label>
                    <Input
                      value={website}
                      onChange={(e) => setWebsite(e.target.value)}
                      placeholder="e.g., acmemanufacturing.com"
                      className="h-11"
                    />
                    <p className="text-xs text-muted-foreground">Used for Apollo enrichment after creation</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Industry</Label>
                    <Select value={industry} onValueChange={setIndustry}>
                      <SelectTrigger className="h-11">
                        <SelectValue placeholder="Select industry..." />
                      </SelectTrigger>
                      <SelectContent>
                        {industries.map((ind) => (
                          <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Engagement Type</Label>
                    <Select value={engagementType} onValueChange={setEngagementType}>
                      <SelectTrigger className="h-11" data-testid="select-engagement-type-prospect">
                        <SelectValue placeholder="If known (can set later)" />
                      </SelectTrigger>
                      <SelectContent>
                        {engagementTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="rounded-lg border p-3">
                    <SourceControl value={origination} onChange={setOrigination} locked={lockedSource ?? null} currentUserId={user?.id} />
                  </div>
                </>
              )}

              {selectedCategory === "clients" && (
                <>
                  <div className="space-y-2">
                    <Label>Engagement Type <span className="text-red-500">*</span></Label>
                    <Select value={engagementType} onValueChange={setEngagementType}>
                      <SelectTrigger className="h-11" data-testid="select-engagement-type">
                        <SelectValue placeholder="What are we helping them with?" />
                      </SelectTrigger>
                      <SelectContent>
                        {engagementTypes.map((type) => (
                          <SelectItem key={type} value={type}>{type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}

              {selectedCategory === "clients" && (
                <IndustryCombobox value={industry} onChange={setIndustry} />
              )}

            </div>
          )}

          {selectedCategory && selectedCategory !== "coi" && (
            <div className="space-y-4 animate-fade-in pt-4 border-t border-border">
              <div>
                <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Primary Contact {selectedCategory === "buyer" && <span className="text-red-500">*</span>}
                </Label>
                <p className="text-sm text-muted-foreground mt-1">
                  {selectedCategory === "buyer"
                    ? "Every company needs at least one contact."
                    : "Add a contact if you have one."}
                </p>
              </div>

              {(selectedCategory === "prospects" || selectedCategory === "clients") && (
                <RadioGroup value={addContact} onValueChange={setAddContact} className="space-y-2">
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="yes" id="add-yes" className="border-primary text-primary-text" />
                    <Label htmlFor="add-yes" className="font-normal cursor-pointer">Add primary contact</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="no" id="add-no" className="border-primary text-primary-text" />
                    <Label htmlFor="add-no" className="font-normal cursor-pointer">Skip for now</Label>
                  </div>
                </RadioGroup>
              )}

              {addContact === "yes" && (
                <div className="space-y-6">
                  {contacts.map((contact, index) => (
                    <div key={contact.id} className="space-y-3 p-4 bg-muted/30 rounded-lg relative">
                      {contacts.length > 1 && (
                        <button
                          onClick={() => removeContact(contact.id)}
                          className="absolute top-3 right-3 p-1 rounded hover:bg-muted"
                        >
                          <X className="w-4 h-4 text-muted-foreground" />
                        </button>
                      )}
                      {index > 0 && (
                        <p className="text-xs font-medium text-muted-foreground">Contact {index + 1}</p>
                      )}
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">First Name <span className="text-red-500">*</span></Label>
                          <Input
                            value={contact.firstName}
                            onChange={(e) => updateContact(contact.id, "firstName", e.target.value)}
                            className="h-9"
                            data-testid={`input-first-name-${index}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Last Name <span className="text-red-500">*</span></Label>
                          <Input
                            value={contact.lastName}
                            onChange={(e) => updateContact(contact.id, "lastName", e.target.value)}
                            className="h-9"
                            data-testid={`input-last-name-${index}`}
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                          <Label className="text-xs">Email <span className="text-red-500">*</span></Label>
                          <Input
                            type="email"
                            value={contact.email}
                            onChange={(e) => updateContact(contact.id, "email", e.target.value)}
                            className="h-9"
                            data-testid={`input-email-${index}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-xs">Phone</Label>
                          <Input
                            type="tel"
                            value={contact.phone}
                            onChange={(e) => updateContact(contact.id, "phone", e.target.value)}
                            className="h-9"
                            data-testid={`input-phone-${index}`}
                          />
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-xs">Title</Label>
                        <Input
                          value={contact.title}
                          onChange={(e) => updateContact(contact.id, "title", e.target.value)}
                          placeholder="e.g., Managing Partner"
                          className="h-9"
                          data-testid={`input-title-${index}`}
                        />
                      </div>
                    </div>
                  ))}

                  <button
                    onClick={addAnotherContact}
                    className="flex items-center gap-2 text-sm text-primary-text hover:underline font-medium"
                    data-testid="add-another-contact"
                  >
                    <Plus className="w-4 h-4" />
                    Add another contact
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-border bg-muted/30 flex-shrink-0 flex items-center justify-end gap-3">
          <Button variant="ghost" onClick={handleClose} disabled={createCompanyMutation.isPending || createCoiMutation.isPending} data-testid="btn-cancel">
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={
              createCompanyMutation.isPending ||
              createCoiMutation.isPending ||
              !selectedCategory ||
              (selectedCategory !== "coi" && !companyName.trim())
            }
            className="gold-gradient text-primary-foreground font-semibold btn-gold"
            data-testid="btn-save"
          >
            {(createCompanyMutation.isPending || createCoiMutation.isPending) ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              "Save"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
